import { FilterParams } from '../../../Entities/Search/QueryParams.entity';

import { filtersSlugToObject } from './FiltersSlugToObject';
import { mergeSlugAndSearchParams } from './MergeSlugAndSearchParams';

/**
 * Appends a "true" value to the "promotions_exclusives" segment in a URL string if it's not already present.
 *
 * @param {string} url - The URL string containing the filter segments, separated by slashes (/).
 * @returns {string} - The updated URL string with "true" added after "promotions_exclusives" if needed.
 */
const appendPromotionsFilterValue = (url: string) => {
  const target = 'promotions_exclusives';
  const parts = url.split('/');

  const index = parts.indexOf(target);
  if (index !== -1 && parts[index + 1] !== 'true') {
    parts.splice(index + 1, 0, 'true');
  }

  return parts.join('/');
};

/**
 * Creates FilterParams from a given URL and route base.
 * Takes both the pathname and search params into account.
 *
 * @param routeBase - The base of the route, e.g. /autos
 */
export const filterParamsFromUrl = (url: URL, _routeBase: string): FilterParams => {
  let routeBase = _routeBase;
  if (!routeBase.startsWith('/')) {
    routeBase = `/${routeBase}`;
  }
  if (!routeBase.endsWith('/')) {
    routeBase = `${routeBase}/`;
  }
  const filtersSlug = url.pathname.split(routeBase)[1] ?? '';

  const updatedFiltersSlug = filtersSlug.includes('promotions_exclusives')
    ? appendPromotionsFilterValue(filtersSlug)
    : filtersSlug;

  const slugParams = filtersSlugToObject(updatedFiltersSlug.split('/'));
  return mergeSlugAndSearchParams(slugParams, searchParamsToObject(url.searchParams));
};

export const searchParamsToObject = (searchParams: URLSearchParams) => {
  const asObject: {
    [key: string]: string | string[];
  } = {};
  searchParams.forEach((value, key) => {
    const existing = asObject[key];

    if (existing === undefined) {
      asObject[key] = value;
      return;
    }

    if (Array.isArray(existing)) {
      existing.push(value);
      return;
    }

    asObject[key] = [existing, value];
  });

  return asObject;
};

export const parseQueryString = (queryString: string) => {
  const searchParams = new URLSearchParams(
    queryString.startsWith('?') ? queryString.slice(1) : queryString,
  );

  return searchParamsToObject(searchParams);
};
